/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ICellRendererComp, ICellRendererParams, IRowNode, KeyCode, ValueFormatterParams } from 'ag-grid-community';

import { ICON_SET } from '../../icon-set';

export interface GroupCellRendererOptions {
  hasChildrenProperty: string;
  isOpenedProperty: string;
  onOpened: ProcedureFn<{ node: IRowNode; open: boolean }>;
  getIndentLevel?: (params: ICellRendererParams) => number;
}

export interface GroupCellRendererParams extends ICellRendererParams, GroupCellRendererOptions {
}

export class GroupCellRenderer implements ICellRendererComp {
  private static buildTemplate(): HTMLElement {
    const wrap = document.createElement('span');
    wrap.className = 'grid-group';
    wrap.innerHTML =
      '<span class="grid-group-expanded"></span>' +
      '<span class="grid-group-contracted"></span>' +
      '<span class="grid-group-value"></span>';
    return wrap;
  }

  private eGui: HTMLElement;
  private eExpanded: HTMLElement;
  private eContracted: HTMLElement;
  private eValue: HTMLElement;

  private params: GroupCellRendererParams;
  private indentClass: string;
  private readonly cleanup: Array<() => void> = [];

  init(params: GroupCellRendererParams): void {
    this.params = params;
    this.eGui = GroupCellRenderer.buildTemplate();
    this.eExpanded = this.eGui.querySelector('.grid-group-expanded') as HTMLElement;
    this.eContracted = this.eGui.querySelector('.grid-group-contracted') as HTMLElement;
    this.eValue = this.eGui.querySelector('.grid-group-value') as HTMLElement;

    this.eExpanded.innerHTML = ICON_SET.GROUP_EXPANDED;
    this.eContracted.innerHTML = ICON_SET.GROUP_CONTRACTED;

    this.addExpandAndContract();
    this.addValueElement();
    this.setupIndent();
  }

  getGui(): HTMLElement {
    return this.eGui;
  }

  destroy(): void {
    this.cleanup.forEach((fn) => fn());
    this.cleanup.length = 0;
  }

  refresh(params: ICellRendererParams): boolean {
    this.params = params as GroupCellRendererParams;
    this.createLeafCell();
    this.setIndent();
    this.showExpandAndContractIcons();
    return true;
  }

  private setupIndent(): void {
    const { getIndentLevel } = this.params;
    if (getIndentLevel) {
      const onIndent = () => this.setIndent();
      onIndent();
      this.params.node.addEventListener('dataChanged', onIndent);
      this.cleanup.push(() => this.params.node.removeEventListener('dataChanged', onIndent));
    } else {
      const onUiLevel = () => this.setIndent();
      this.params.node.addEventListener('uiLevelChanged', onUiLevel);
      this.cleanup.push(() => this.params.node.removeEventListener('uiLevelChanged', onUiLevel));
      this.setIndent();
    }
  }

  private setIndent(): void {
    const paddingCount = this.params.getIndentLevel
      ? Math.max(0, Math.min(40, this.params.getIndentLevel(this.params)))
      : (this.params.node.uiLevel ?? 0);
    const cellEl = this.getGui();

    if (this.indentClass) {
      cellEl.classList.remove(this.indentClass);
    }

    this.indentClass = 'grid-group-indent-' + paddingCount;
    cellEl.classList.add(this.indentClass);
  }

  private addExpandAndContract(): void {
    const params = this.params;
    const eGroupCell = params.eGridCell;

    const onExpandClick = (e: MouseEvent) => this.onExpandClicked(e);
    this.eExpanded.addEventListener('click', onExpandClick);
    this.eContracted.addEventListener('click', onExpandClick);
    this.cleanup.push(() => {
      this.eExpanded.removeEventListener('click', onExpandClick);
      this.eContracted.removeEventListener('click', onExpandClick);
    });

    const onKeyDown = (e: KeyboardEvent) => this.onKeyDown(e);
    eGroupCell.addEventListener('keydown', onKeyDown);
    this.cleanup.push(() => eGroupCell.removeEventListener('keydown', onKeyDown));

    const onData = () => {
      this.createLeafCell();
      this.setIndent();
      this.showExpandAndContractIcons();
    };
    params.node.addEventListener('dataChanged', onData);
    this.cleanup.push(() => params.node.removeEventListener('dataChanged', onData));
    this.showExpandAndContractIcons();
  }

  private addValueElement(): void {
    this.createLeafCell();
  }

  private createLeafCell(): void {
    const field = this.params.column?.getColDef().field;
    const v = field ? this.params.node.data?.[field] : this.params.value;
    let display: string | number | undefined = v;
    const colDef = this.params.column?.getColDef();
    if (
      v !== null && v !== undefined && v !== ''
      && colDef?.valueFormatter
      && typeof colDef.valueFormatter === 'function'
      && this.params.column
    ) {
      display = colDef.valueFormatter({
        ...this.params,
        value: v,
        column: this.params.column,
      } as ValueFormatterParams);
    } else if (this.params.valueFormatted != null && v === this.params.value) {
      display = this.params.valueFormatted;
    }
    if (display !== null && display !== undefined && display !== '') {
      this.eValue.innerHTML = String(display);
    } else {
      this.eValue.innerHTML = '&nbsp;';
    }
  }

  private onKeyDown(event: KeyboardEvent): void {
    if (event.key === KeyCode.ENTER) {
      const cellEditable = this.params.column?.isCellEditable(this.params.node);
      if (cellEditable) {
        return;
      }
      event.preventDefault();
      this.onExpandOrContract();
    }
  }

  private onExpandClicked(mouseEvent: MouseEvent): void {
    if ((mouseEvent as any).agGridSuppressPropagation) {
      return;
    }
    this.onExpandOrContract();
  }

  private onExpandOrContract(): void {
    const { node, hasChildrenProperty, isOpenedProperty, onOpened } = this.params;

    if (node.data[hasChildrenProperty]) {
      onOpened({ node, open: !node.data[isOpenedProperty] });
    }
  }

  private isExpandable(): boolean {
    const { node, hasChildrenProperty } = this.params;
    return node.data[hasChildrenProperty];
  }

  private showExpandAndContractIcons(): void {
    const { node, isOpenedProperty } = this.params;

    if (this.isExpandable()) {
      const expanded = node.data[isOpenedProperty];
      this.eExpanded.classList.toggle('ag-hidden', !expanded);
      this.eContracted.classList.toggle('ag-hidden', expanded);
    } else {
      this.eExpanded.classList.add('ag-hidden');
      this.eContracted.classList.add('ag-hidden');
    }
  }
}
